import { useEffect, useState } from 'react';
import { get } from '../api';
import { fmtShort, pad, PERSON_UNIT, slotLabel, todayIso } from '../lib';
import { Empty, Loading, useFail, useToast } from '../ui';

/* Análise detalhada (só o Administrador): Notebooks, Chaves, Visitantes e Biblioteca, cada uma com filtros,
   destaques, gráficos, tabela dos registros e exportação em CSV (abre no Excel). */

type R = Record<string, any> & { date: string; dow: number };
export interface ExtData { dates: string[]; nb: R[]; keys: R[]; vis: R[]; lib: R[] }
type Pair = [string | number, number];
interface Cfg {
  filters: [string, string, ((v: string) => string)?][];
  search: (x: R) => string; searchPh: string;
  tiles: (r: R[]) => [string, string | number, string][];
  bars: (r: R[]) => [string, string, Pair[], ((k: any) => string)?][];
  cols: [string, (x: R) => string | number | null | undefined][];
}

const DOW_LONG = ['Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta'];
const pct = (v: number) => Math.round(v * 100) + '%';
const hourLbl = (h: number) => `${pad(7 + h)}h`;
const pairsBy = (recs: R[], f: (x: R) => any, w?: (x: R) => number): Pair[] => {
  const m = new Map<any, number>();
  for (const x of recs) { const k = f(x); if (k === '' || k == null) continue; m.set(k, (m.get(k) ?? 0) + (w ? w(x) : 1)); }
  return [...m.entries()].sort((a, b) => b[1] - a[1]);
};
const top1 = (recs: R[], f: (x: R) => any, w?: (x: R) => number): Pair => pairsBy(recs, f, w)[0] ?? ['—', 0];
const avg = (a: number[]) => a.length ? Math.round(a.reduce((x, y) => x + y, 0) / a.length) : 0;
const minsLbl = (m: number) => m >= 60 ? `${Math.floor(m / 60)}h${m % 60 ? pad(m % 60) : ''}` : `${m} min`;
const unitLbl = (v: string) => PERSON_UNIT[v] ?? v;
const byHour = (R: R[]) => pairsBy(R, x => Math.floor((x.start ?? 0) / 12)).sort((a, b) => +a[0] - +b[0]);
const byDow = (R: R[]) => pairsBy(R, x => x.dow).sort((a, b) => +a[0] - +b[0]);

export const SECTIONS: Record<string, { label: string; key: keyof Omit<ExtData, 'dates'>; cfg: Cfg }> = {
  notebooks: { label: 'Notebooks', key: 'nb', cfg: {
    filters: [['origin', 'Origem'], ['kind', 'Tipo'], ['unit', 'Unidade', unitLbl], ['cls', 'Turma'], ['teacher', 'Professor(a) ou colaborador(a)']],
    search: x => [x.teacher, x.student, x.cls, x.nb].join(' '), searchPh: 'Aluno(a), notebook...',
    tiles: R => [
      ['Movimentações', R.length, `${R.reduce((a, x) => a + x.qty, 0)} notebooks no total`],
      ['Quem mais usa', top1(R, x => x.teacher, x => x.qty)[0], `${top1(R, x => x.teacher, x => x.qty)[1]} notebooks`],
      ['Horário mais procurado', (k => k === '—' ? '—' : hourLbl(+k))(top1(R, x => Math.floor(x.start / 12))[0]), `${top1(R, x => Math.floor(x.start / 12))[1]} movimentações começam nessa hora`],
      ['Turma que mais usa', top1(R, x => x.cls, x => x.qty)[0], `${top1(R, x => x.cls, x => x.qty)[1]} notebooks`],
      ['Aluno(a) que mais retira', top1(R, x => x.student)[0], `${top1(R, x => x.student)[1]} empréstimos`],
      ['Notebook mais usado', top1(R, x => x.nb)[0], `${top1(R, x => x.nb)[1]} empréstimos individuais`],
    ],
    bars: R => [
      ['Professores e colaboradores que mais usam', 'Quantidade de notebooks', pairsBy(R, x => x.teacher, x => x.qty)],
      ['Horários mais reservados', 'Hora de início', byHour(R), hourLbl],
      ['Turmas', 'Quantidade de notebooks', pairsBy(R, x => x.cls, x => x.qty)],
      ['Alunos e alunas que mais retiram', 'Empréstimos individuais', pairsBy(R, x => x.student)],
      ['Notebooks mais usados', 'Empréstimos individuais', pairsBy(R, x => x.nb)],
      ['Por tipo e origem', '', pairsBy(R, x => `${x.kind} · ${x.origin}`)],
    ],
    cols: [['Data', x => fmtShort(x.date)], ['Início', x => slotLabel(x.start)], ['Tipo', x => x.kind], ['Origem', x => x.origin], ['Professor(a)/colaborador(a)', x => x.teacher],
      ['Turma', x => x.cls], ['Aluno(a)', x => x.student], ['Notebook', x => x.nb], ['Qtd.', x => x.qty]],
  } },
  chaves: { label: 'Chaves', key: 'keys', cfg: {
    filters: [['point', 'Ponto de retirada'], ['key', 'Chave'], ['who', 'Quem retirou']],
    search: x => [x.key, x.who, x.owner].join(' '), searchPh: 'Chave ou pessoa',
    tiles: R => {
      const late = R.filter(x => x.late).length, durs = R.map(x => x.mins).filter(m => m != null && m > 0);
      return [['Retiradas', R.length, `${new Set(R.map(x => x.key)).size} chaves diferentes`], ['Devoluções atrasadas', pct(late / R.length), `${late} de ${R.length}`],
        ['Chave mais retirada', top1(R, x => x.key)[0], `${top1(R, x => x.key)[1]} retiradas`], ['Quem mais retira', top1(R, x => x.who)[0], `${top1(R, x => x.who)[1]} retiradas`],
        ['Tempo médio com a chave', minsLbl(avg(durs)), 'Da retirada à devolução'], ['Quem mais atrasa', top1(R.filter(x => x.late), x => x.who)[0], `${top1(R.filter(x => x.late), x => x.who)[1]} atrasos`]];
    },
    bars: R => [
      ['Chaves mais retiradas', '', pairsBy(R, x => x.key)],
      ['Pessoas que mais retiram', '', pairsBy(R, x => x.who)],
      ['Devoluções atrasadas por pessoa', '', pairsBy(R.filter(x => x.late), x => x.who)],
      ['Retiradas por horário', 'Hora da retirada', byHour(R), hourLbl],
      ['Por ponto de retirada', '', pairsBy(R, x => x.point)],
      ['Por dia da semana', '', byDow(R), k => DOW_LONG[k]],
    ],
    cols: [['Data', x => fmtShort(x.date)], ['Retirada', x => slotLabel(x.start)], ['Chave', x => x.key], ['Ponto', x => x.point], ['Retirado por', x => x.who], ['Reserva de', x => x.owner],
      ['Tempo', x => x.mins != null && x.mins > 0 ? minsLbl(x.mins) : ''], ['Atrasada', x => x.late ? 'Sim' : 'Não']],
  } },
  visitantes: { label: 'Visitantes', key: 'vis', cfg: {
    filters: [['type', 'Tipo'], ['org', 'Empresa ou família'], ['card', 'Cartão']],
    search: x => [x.name, x.org].join(' '), searchPh: 'Nome do visitante',
    tiles: R => [
      ['Visitas', R.length, `${new Set(R.map(x => x.org)).size} empresas e famílias`], ['Permanência média', minsLbl(avg(R.map(x => x.mins).filter(m => m != null))), 'Da entrada à saída'],
      ['Empresa mais frequente', top1(R.filter(x => x.type === 'Empresa'), x => x.org)[0], `${top1(R.filter(x => x.type === 'Empresa'), x => x.org)[1]} visitas`],
      ['Horário de pico', (k => k === '—' ? '—' : hourLbl(+k))(top1(R, x => Math.floor(x.start / 12))[0]), `${top1(R, x => Math.floor(x.start / 12))[1]} chegadas nessa hora`],
      ['Dia mais movimentado', (k => k === '—' ? '—' : DOW_LONG[+k])(top1(R, x => x.dow)[0]), `${top1(R, x => x.dow)[1]} visitas`],
      ['Chegaram em grupo', pct(R.filter(x => x.group).length / R.length), `${R.filter(x => x.group).length} visitantes`],
    ],
    bars: R => [
      ['Empresas mais frequentes', '', pairsBy(R.filter(x => x.type === 'Empresa'), x => x.org)],
      ['Famílias mais frequentes', '', pairsBy(R.filter(x => x.type === 'Família'), x => x.org)],
      ['Chegadas por horário', '', byHour(R), hourLbl],
      ['Visitas por dia da semana', '', byDow(R), k => DOW_LONG[k]],
      ['Tempo de permanência', '', pairsBy(R.filter(x => x.mins != null), x => x.mins < 30 ? 0 : x.mins < 60 ? 1 : x.mins < 120 ? 2 : 3).sort((a, b) => +a[0] - +b[0]), k => ['Até 30 min', '30 min a 1h', '1h a 2h', 'Mais de 2h'][k]],
      ['Cartões mais usados', '', pairsBy(R, x => x.card)],
    ],
    cols: [['Data', x => fmtShort(x.date)], ['Entrada', x => slotLabel(x.start)], ['Visitante', x => x.name], ['Tipo', x => x.type], ['Empresa/família', x => x.org], ['Cartão', x => x.card],
      ['Permanência', x => x.mins != null ? minsLbl(x.mins) : 'No colégio'], ['Foto', x => x.photo ? 'Sim' : 'Não']],
  } },
  biblioteca: { label: 'Biblioteca', key: 'lib', cfg: {
    filters: [['type', 'Tipo'], ['unit', 'Unidade', unitLbl], ['cls', 'Turma'], ['title', 'Título']],
    search: x => [x.title, x.student, x.cls].join(' '), searchPh: 'Título ou aluno(a)',
    tiles: R => [
      ['Empréstimos', R.length, `${R.filter(x => x.type === 'Livro').length} livros e ${R.filter(x => x.type === 'Jogo').length} jogos`],
      ['Ainda emprestados', R.filter(x => x.out).length, 'Sem devolução registrada'],
      ['Tempo médio com o aluno(a)', `${avg(R.map(x => x.days).filter(v => v != null))} dias`, 'Da retirada à devolução'],
      ['Livro mais emprestado', top1(R.filter(x => x.type === 'Livro'), x => x.title)[0], `${top1(R.filter(x => x.type === 'Livro'), x => x.title)[1]} empréstimos`],
      ['Jogo mais emprestado', top1(R.filter(x => x.type === 'Jogo'), x => x.title)[0], `${top1(R.filter(x => x.type === 'Jogo'), x => x.title)[1]} empréstimos`],
      ['Turma que mais lê', top1(R.filter(x => x.type === 'Livro'), x => x.cls)[0], `${top1(R.filter(x => x.type === 'Livro'), x => x.cls)[1]} livros`],
    ],
    bars: R => [
      ['Livros mais emprestados', '', pairsBy(R.filter(x => x.type === 'Livro'), x => x.title)],
      ['Jogos mais emprestados', '', pairsBy(R.filter(x => x.type === 'Jogo'), x => x.title)],
      ['Alunos e alunas que mais pegam', '', pairsBy(R, x => x.student)],
      ['Turmas', '', pairsBy(R, x => x.cls)],
      ['Por unidade', '', pairsBy(R, x => x.unit), unitLbl],
      ['Empréstimos por dia da semana', '', byDow(R), k => DOW_LONG[k]],
    ],
    cols: [['Data', x => fmtShort(x.date)], ['Tipo', x => x.type], ['Título', x => x.title], ['Aluno(a)', x => x.student], ['Turma', x => x.cls], ['Unidade', x => unitLbl(x.unit)],
      ['Devolução', x => x.out || x.days == null ? 'Emprestado' : `${x.days} dia${x.days === 1 ? '' : 's'}`]],
  } },
};

/** Arquivo CSV com ponto e vírgula e BOM (abre direto no Excel em português) */
function exportCsv(sec: string, cfg: Cfg, recs: R[]) {
  const q = (v: unknown) => { const t = String(v ?? ''); return /[";\n]/.test(t) ? `"${t.replace(/"/g, '""')}"` : t; };
  const csv = [cfg.cols.map(([h]) => q(h)).join(';'), ...recs.map(x => cfg.cols.map(([, fn]) => q(fn(x))).join(';'))].join('\r\n');
  const url = URL.createObjectURL(new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url; a.download = `conves-${sec}-${todayIso()}.csv`; document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

const Bar = ({ lb, v, max }: { lb: string; v: number; max: number }) => (
  <div className="bar"><span className="lb" title={lb}>{lb}</span><span className="tr"><i style={{ width: Math.round(v / (max || 1) * 100) + '%' }} /></span><span className="v">{v}</span></div>
);

export function ExtSection({ sec, days }: { sec: string; days: number }) {
  const fail = useFail(), toast = useToast();
  const [data, setData] = useState<ExtData | null>(null);
  const [f, setF] = useState<Record<string, string>>({});
  useEffect(() => { setData(null); get<ExtData>('reports/extra?days=' + days).then(setData).catch(fail); }, [days, fail]);
  useEffect(() => setF({}), [sec]);
  if (!data) return <Loading />;
  const { cfg, key } = SECTIONS[sec];
  const all = data[key];
  const recs = all.filter(x => cfg.filters.every(([k]) => !f[k] || String(x[k]) === f[k]) && (!f.q || cfg.search(x).toLowerCase().includes(f.q.toLowerCase())));
  const anyF = cfg.filters.some(([k]) => f[k]) || f.q;
  const opts = (k: string) => [...new Set(all.map(x => x[k]).filter(v => v !== '' && v != null))].sort((a, b) => String(a).localeCompare(String(b), 'pt'));
  const filters = (
    <div className="afilters">
      {cfg.filters.map(([k, lbl, fmt]) => (
        <div className="field" key={k}><label htmlFor={'rf_' + k}>{lbl}</label>
          <select id={'rf_' + k} value={f[k] ?? ''} onChange={e => setF({ ...f, [k]: e.target.value })}><option value="">Todos</option>
            {opts(k).map(v => <option key={String(v)} value={String(v)}>{fmt ? fmt(String(v)) : String(v)}</option>)}</select></div>
      ))}
      <div className="field"><label htmlFor="rf_q">Buscar</label><input id="rf_q" value={f.q ?? ''} placeholder={cfg.searchPh} onChange={e => setF({ ...f, q: e.target.value })}
        style={{ border: '1px solid var(--line)', borderRadius: 8, padding: '6px 10px', height: 34, background: 'var(--surface)' }} /></div>
      <button type="button" className="btn ghost sm" disabled={!anyF} onClick={() => setF({})}>Limpar filtros</button>
      <button type="button" className="btn sm" disabled={!recs.length} onClick={() => { exportCsv(sec, cfg, recs); toast('Arquivo CSV baixado. Ele abre no Excel.'); }}>Exportar dados</button>
      <span className="hint"><b>{recs.length}</b> registro{recs.length === 1 ? '' : 's'} no recorte</span>
    </div>
  );
  if (!recs.length) return <>{filters}<section className="panel"><Empty>Nenhum registro para esse recorte. Tente outro filtro ou um período maior.</Empty></section></>;
  const rows = recs.slice().sort((a, b) => b.date.localeCompare(a.date) || (b.start ?? 0) - (a.start ?? 0)).slice(0, 150);
  return <>
    {filters}
    <div className="hl">{cfg.tiles(recs).map(([l, v, s]) => <div className="tile" key={l}><span>{l}</span><b>{String(v)}</b><small>{s}</small></div>)}</div>
    <div className="rgrid">
      {cfg.bars(recs).map(([title, sub, pairs, lab]) => (
        <section className="panel" key={title}><div className="panel-h"><h2>{title}</h2>{sub && <span className="sub">{sub}</span>}</div>
          <div className="rbody"><div className="bars">{pairs.length ? pairs.slice(0, 12).map(([k, v]) => <Bar key={String(k)} lb={lab ? lab(k) : String(k)} v={v} max={pairs[0][1]} />)
            : <span className="mine-empty">Sem dados no recorte.</span>}</div></div></section>
      ))}
      <section className="panel span2"><div className="panel-h"><h2>Registros</h2><span className="sub">Os {rows.length} mais recentes do recorte · use "Exportar dados" para levar tudo para o Excel</span></div>
        <div className="rbody tblbox"><table><thead><tr>{cfg.cols.map(([h]) => <th key={h}>{h}</th>)}</tr></thead>
          <tbody>{rows.map((x, i) => <tr key={i}>{cfg.cols.map(([h, fn]) => <td key={h}>{String(fn(x) ?? '')}</td>)}</tr>)}</tbody></table></div></section>
    </div>
  </>;
}
